import React, { useState, useEffect, useRef } from 'react'
import Router, { useRouter } from 'next/router'
import { useViewport } from 'utils'

// swiper
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Lazy, Pagination, Navigation } from 'swiper'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

// next component
import Head from 'next/head'

// components
import NFTMerchSection from 'components/pages/BrowseNFT/MainSection/NFTMerchSection'
import ImageExpandModal from 'components/Modal/ImageExpandModal'
import MerchOrderMain from 'components/pages/MerchOrder/MerchOrderMain'
import SecondaryLayout from 'components/Layout/SecondaryLayout'
import ImageWithFoster from 'components/ImageWithFoster'
import MerchOrderShipping from 'components/pages/MerchOrder/MerchOrderShipping'
import { useDispatch } from 'react-redux'

// resources
import ArrowLeftIcon from 'assets/icons/arrow-left-24x24.svg?component'
import LeftBtnIcon from 'assets/icons/arrow-left-17x13.svg?component'
import RightBtnIcon from 'assets/icons/arrow-right-17x13.svg?component'
import ExpandIcon from 'assets/icons/expand-icon.svg?component'

// styles
import styles from './index.module.scss'

interface MerchOrderPageProps {
  publicArtistData?: any
  productDetail?: any
  error?: string
}

const MerchOrderPage = ({ publicArtistData, productDetail }: MerchOrderPageProps) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const viewport = useViewport()
  const [isImageExpand, setIsImageExpand] = useState(false)

  let imageList = [],
    rawUrlList = []
  let type = ''
  if (productDetail?.fulfillment_type === 'foster') {
    productDetail?.options.addons.map((item, index) => {
      imageList.push({ id: index, url: item.mockup_url, selected: index === 0 })
      rawUrlList.push({ url: item.raw_url })
    })
    type = 'foster'
  } else {
    productDetail?.options.product_images.map((item, index) => {
      imageList.push({ id: index, url: item, selected: index === 0 })
    })
    if (productDetail?.master_edition_address === null) type = 'physical'
  }

  const [merchList, setMerchList] = useState([])
  const [selectedMerch, setSelectedMerch] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [swiper, setSwiper] = useState(null)

  const [printData, setPrintData] = useState(null)

  useEffect(() => {
    if (productDetail === undefined) {
      router.push('/404')
      return
    }

    if (productDetail) {
      let list = []
      if (productDetail.fulfillment_type === 'foster') {
        productDetail?.options.addons.map((item, index) => {
          list.push({ id: index, url: item.mockup_url, selected: index === 0 })
        })
      } else {
        productDetail.options.product_images.map((item, index) => {
          list.push({ id: index, url: item, selected: index === 0 })
        })
      }
      if (list.length > 0) {
        setMerchList(list)
        setSelectedMerch(list[0])
      }
    }

    let print_data = {
      id: 1,
      quantity: 1,
      size: 'L',
      name: productDetail.name,
      description: productDetail.description,
      technique: productDetail.options.print_technique,
      cost: productDetail.selling_price / 100,
    }

    if (productDetail.product_type_id === 0) {
      print_data['color'] = productDetail.options.colors.length > 0 ? productDetail.options.colors[0] : 'white'
    } else {
      print_data['color'] = productDetail.options.color
    }
    setPrintData(print_data)
  }, [productDetail, router])

  const onClickMerch = key => {
    let list = []
    merchList.map((merch, index) => {
      if (key === index) {
        list.push({ id: index, url: merch.url, selected: true })
        setSelectedMerch({ id: index, url: merch.url, selected: true })
      } else {
        list.push({ id: index, url: merch.url, selected: false })
      }
    })
    setMerchList(list)
    if (viewport === 'xs' && swiper) swiper.slideTo(key + 1)
  }

  const onClickBuyNow = () => {
    setCurrentStep(2)
  }

  const handleOnChange = () => {
    if (swiper) {
      if (swiper.activeIndex === undefined) {
        merchList.map((merch, index) => {
          if (index === 0) merch.selected = true
          else merch.selected = false
        })
        setSelectedMerch(merchList[0])
      } else {
        let key = (swiper.activeIndex - 1) % merchList.length
        if (swiper.activeIndex == 0) key = merchList.length - 1

        setSelectedMerch(merchList[key])

        let list = []
        merchList.map((merch, index) => {
          if (key === index) {
            list.push({ id: index, url: merch.url, selected: true })
            setSelectedMerch({ id: index, url: merch.url, selected: true })
          } else {
            list.push({ id: index, url: merch.url, selected: false })
          }
        })
        setMerchList(list)
      }
    }
  }

  let pageTitle = productDetail?.name ?? '<not found>'
  let pageDescription = productDetail?.description ?? '<not found>'

  const sidebarRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [headerHeight] = useState(80)
  const [sidebarHeight, setSidebarHeight] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    setViewportHeight(window.innerHeight)

    const updateSidebarHeight = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.scrollHeight)
        setViewportHeight(window.innerHeight)
        setIsInitialized(true)
      }
    }

    updateSidebarHeight()
    window.addEventListener('resize', updateSidebarHeight)
    return () => window.removeEventListener('resize', updateSidebarHeight)
  }, [])

  useEffect(() => {
    if (!sidebarRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setSidebarHeight(entry.contentRect.height)
        setViewportHeight(window.innerHeight)
      }
    })

    resizeObserver.observe(sidebarRef.current)

    return () => resizeObserver.disconnect()
  }, [sidebarRef])

  useEffect(() => {
    if (!isInitialized) return

    const handleScroll = () => {
      if (!sidebarRef.current || !contentRef.current) return

      const scrollTop = window.scrollY
      const sidebarScrollHeight = sidebarRef.current.scrollHeight
      const sidebarVisibleHeight = viewportHeight - headerHeight
      const maxSidebarScroll = Math.max(0, sidebarScrollHeight - sidebarVisibleHeight)

      const sidebarTransform = Math.min(scrollTop, maxSidebarScroll)

      requestAnimationFrame(() => {
        if (sidebarRef.current) {
          sidebarRef.current.style.transform = `translateY(-${sidebarTransform}px)`
        }

        if (scrollTop <= maxSidebarScroll) {
          window.scrollTo(0, scrollTop)
          if (contentRef.current) {
            contentRef.current.style.transform = 'translateY(0)'
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [headerHeight, sidebarHeight, isInitialized, viewportHeight])

  return (
    <div className={`w-full block lg:relative lg:min-h-screen ${styles.container}`}>
      <Head>
        <title>{pageTitle}</title>
        <meta property="description" content={pageDescription} />

        <meta name="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {imageList.map(({ url }, index) => (
          <meta key={index} name="og:image" content={url} />
        ))}

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        {imageList.map(({ url }, index) => (
          <meta key={index} property="twitter:image" content={url} />
        ))}
      </Head>

      <div
        ref={contentRef}
        className="w-full lg:flex"
        style={{
          minHeight: `${Math.max(viewportHeight, sidebarHeight)}px`,
          willChange: 'transform',
        }}
      >
        <div
          className={`lg:w-[calc(100vw-650px)] w-full lg:h-[calc(100vh-80px)] lg:sticky lg:top-[80px] overflow-hidden ${
            styles.leftBG
          } ${viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '' : 'g-left-container'} lg:pr-[55px]`}
        >
          <div className="w-full flex">
            <div className="hidden lg:block mt-[75px] min-w-[130px]">
              <div className="lg:block hidden">
                <div className={styles.backStore} onClick={() => router.push(`/${router.query.id}/merch`)}>
                  <ArrowLeftIcon />
                  <div>Back&nbsp;to&nbsp;store</div>
                </div>
              </div>

              {productDetail ? (
                <div className="lg:flex hidden w-[75px] h-[75px] flex flex-col gap-[11px] mt-[30px]">
                  {merchList.length > 1 &&
                    merchList.map((merch, index) => {
                      return (
                        <div
                          key={index}
                          onClick={() => onClickMerch(index)}
                          className={merch.selected ? styles.image : styles.imageHover}
                        >
                          <div className={merch.selected ? styles.selected : styles.unselected}>
                            <ImageWithFoster
                              width={350}
                              height={350}
                              quality={100}
                              src={merch.url}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )
                    })}
                </div>
              ) : (
                <div className="mt-[30px] lg:block hidden">
                  <div className="w-[75px] h-[75px] rounded-[10px] overflow-hidden">
                    <div className="skeletonLoader" />
                  </div>
                  <div className="w-[75px] h-[75px] rounded-[10px] overflow-hidden mt-[11px]">
                    <div className="skeletonLoader" />
                  </div>
                  <div className="w-[75px] h-[75px] rounded-[10px] overflow-hidden mt-[11px]">
                    <div className="skeletonLoader" />
                  </div>
                </div>
              )}
            </div>
            <div className="w-full lg:w-auto lg:flex-1 flex justify-center">
              <div className={`w-full flex justify-center lg:max-w-[625px]`}>
                <div className={`lg:hidden block h-auto aspect-square w-full`}>
                  {viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? (
                    <div className="relative">
                      <Swiper
                        slidesPerView={1}
                        spaceBetween={10}
                        pagination={{
                          clickable: true,
                        }}
                        onSlideChange={handleOnChange}
                        onSwiper={setSwiper}
                        className={styles.swiperContainer}
                        modules={[Autoplay, Lazy, Navigation, Pagination]}
                        loop={merchList.length > 1}
                      >
                        {merchList.map((item, index) => (
                          <SwiperSlide key={index}>
                            <div className={styles.imageArea}>
                              <div
                                className={styles.imageContent}
                                style={{
                                  background:
                                    type === 'physical' ? 'rgba(var(--main-bg-color-rgb), 0.05)' : 'transparent',
                                  overflow: 'hidden',
                                }}
                              >
                                <ImageWithFoster
                                  width={700}
                                  quality={100}
                                  src={item.url}
                                  style={{
                                    paddingTop: type === 'foster' ? '15px' : '0px',
                                    paddingBottom: type === 'foster' ? '15px' : '0px',
                                  }}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>

                      {merchList.length > 1 && (
                        <div
                          className={`${styles.indicatorArea} absolute bottom-[15px] sm:flex flex items-center z-[10]`}
                        >
                          <div className="gap-[14px] h-[5px] flex justify-around mt-[-1px]">
                            {merchList.map((item, index) => (
                              <div
                                key={index}
                                className={item.selected ? styles.selectedMobile : styles.nonSelectedMobile}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.mobileImageArea}>
                      <div className={styles.imageArea}>
                        <div
                          className={styles.imageContent}
                          style={{
                            background: type === 'physical' ? 'rgba(var(--main-bg-color-rgb), 0.05)' : 'transparent',
                          }}
                        >
                          <ImageWithFoster
                            width={700}
                            quality={100}
                            src={selectedMerch ? selectedMerch.url : ''}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={`lg:block hidden sm:w-[640px] sm:h-auto lg:w-[300px] xl:w-[500px] 2xl:w-[625px] mt-[20px] lg:mt-[75px]`}
                >
                  <div className={styles.imageArea}>
                    <div
                      className={styles.imageContent}
                      style={{
                        background: type === 'physical' ? 'rgba(var(--main-bg-color-rgb), 0.05)' : 'transparent',
                        borderRadius: '15px',
                        overflow: 'hidden',
                      }}
                    >
                      <ImageWithFoster
                        width={700}
                        quality={100}
                        src={selectedMerch ? selectedMerch.url : ''}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {merchList.length > 1 && selectedMerch && (
                    <div className="w-full mt-[30px] flex justify-center select-none">
                      <div className="flex gap-[50px]">
                        <div
                          className={styles.arrowButton}
                          onClick={() =>
                            selectedMerch.id > 0
                              ? onClickMerch(selectedMerch.id - 1)
                              : onClickMerch(merchList.length - 1)
                          }
                        >
                          <LeftBtnIcon />
                        </div>
                        <div
                          className={styles.arrowButton}
                          onClick={() =>
                            selectedMerch.id < merchList.length - 1
                              ? onClickMerch(selectedMerch.id + 1)
                              : onClickMerch(0)
                          }
                        >
                          <RightBtnIcon />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.explandIcon} onClick={() => setIsImageExpand(true)}>
              <ExpandIcon />
            </div>
          </div>
        </div>
        <div className="w-full h-auto lg:w-[650px] lg:h-[calc(100vh-80px)] overflow-hidden lg:sticky lg:top-[80px]">
          <div
            ref={['xs', 'sm', 'md'].includes(viewport) ? undefined : sidebarRef}
            style={['xs', 'sm', 'md'].includes(viewport) ? { transform: 'translateY(0)' } : { willChange: 'transform' }}
            className={`w-full z-10 pt-[15px] lg:pt-[0px] ${
              viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? 'g-nft-container' : 'g-nft-right-container'
            } lg:pl-[100px] lg:pb-[50px]`}
          >
            <div className="w-full lg:mb-[50px] lg:mt-[0px]">
              {currentStep === 1 &&
                (productDetail ? (
                  <div className="w-full mt-[0px] lg:mt-[70px]">
                    <MerchOrderMain
                      username={router.query.id}
                      type={type}
                      productDetail={productDetail}
                      userDetail={publicArtistData}
                      onClickBuyNow={onClickBuyNow}
                      setQuantity={quantity => {
                        let newData = { ...printData }
                        newData.quantity = quantity
                        newData.cost = (productDetail?.selling_price / 100) * quantity
                        setPrintData(newData)
                      }}
                      setMerchSize={size => {
                        setPrintData({ ...printData, size: size })
                      }}
                      setMerchColor={color => {
                        setPrintData({ ...printData, color: color })
                      }}
                      productId={router.query.product_id}
                      data={printData}
                      setData={data => {
                        let newData = { ...printData }
                        newData.name = data.name
                        newData.description = data.description
                        setPrintData(newData)
                      }}
                      merchList={merchList}
                      rawList={rawUrlList}
                    />
                  </div>
                ) : (
                  <div>
                    <div className="lg:block hidden mt-[75px]">
                      <div className={styles.skeltonContainer}>
                        <div className="w-full h-[700px] flex flex-col justify-between">
                          <div className="w-full">
                            <div className="w-full flex justify-between">
                              <div className="w-[200px] h-[35px] rounded-full overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                              <div className="w-[65px] h-[35px] rounded-full overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                            </div>

                            <div className="mt-[35px] flex gap-[20px]">
                              <div className="w-[150px] h-[35px] rounded-full overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                              <div className="w-[110px] h-[35px] rounded-full overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                              <div className="w-[70px] h-[35px] rounded-full overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                            </div>

                            <div className="w-full h-[70px] rounded-[10px] overflow-hidden mt-[58px]">
                              <div className="skeletonLoader" />
                            </div>

                            <div className="w-[250px] h-[30px] rounded-[10px] overflow-hidden mt-[58px]">
                              <div className="skeletonLoader" />
                            </div>

                            <div className="w-full h-[45px] rounded-[10px] overflow-hidden mt-[10px]">
                              <div className="skeletonLoader" />
                            </div>

                            <div className="w-full h-[45px] rounded-[10px] overflow-hidden mt-[10px]">
                              <div className="skeletonLoader" />
                            </div>

                            <div className="w-full flex justify-between mt-[58px]">
                              <div className="w-[150px] h-[30px] rounded-[10px] overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                            </div>
                            <div className="w-full flex justify-between mt-[20px]">
                              <div className="w-[200px] h-[30px] rounded-[10px] overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                            </div>
                            <div className="w-full flex justify-between mt-[20px]">
                              <div className="w-[150px] h-[30px] rounded-[10px] overflow-hidden">
                                <div className="skeletonLoader" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-[20px] lg:hidden block">
                      <div className="w-full flex justify-between">
                        <div className="w-[200px] h-[35px] rounded-[10px] overflow-hidden">
                          <div className="skeletonLoader" />
                        </div>
                        <div className="w-[80px] h-[35px] rounded-[10px] overflow-hidden">
                          <div className="skeletonLoader" />
                        </div>
                      </div>
                      <div className="flex gap-[15px] mt-[20px]">
                        <div className="w-[140px] h-[40px] rounded-full overflow-hidden ">
                          <div className="skeletonLoader" />
                        </div>
                        <div className="w-[140px] h-[40px] rounded-full overflow-hidden ">
                          <div className="skeletonLoader" />
                        </div>
                      </div>
                      <div className="w-full h-[150px] mt-[20px] rounded-[10px] overflow-hidden">
                        <div className="skeletonLoader" />
                      </div>
                    </div>
                  </div>
                ))}
              {currentStep === 2 && (
                <div className="w-full mt-[0px] lg:mt-[57px]">
                  <MerchOrderShipping
                    productId={router.query.product_id}
                    type={type}
                    onBack={info => {
                      setCurrentStep(1)
                    }}
                    onNext={orderId => {
                      let data = {
                        username: router.query.id,
                      }
                      dispatch({ type: 'set', merchData: data })
                      router.push(`/orders/${orderId}`)
                    }}
                    data={printData}
                    merchList={merchList}
                    rawList={rawUrlList}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full lg:mt-[45px]">
        <NFTMerchSection username={router.query.id} userId={publicArtistData?.id} label="You may also like" />
      </div>

      {isImageExpand && (
        <ImageExpandModal open={isImageExpand} close={() => setIsImageExpand(false)} image={selectedMerch?.url} />
      )}
    </div>
  )
}

export default MerchOrderPage

const LayoutWrapper = ({ children }: { children }) => {
  const viewport = useViewport()
  const isMobile = viewport === 'xs' || viewport === 'sm' || viewport === 'md'

  return <SecondaryLayout style={{ overflow: isMobile ? 'hidden' : 'visible' }}>{children}</SecondaryLayout>
}

MerchOrderPage.getLayout = function getLayout(page) {
  return <LayoutWrapper>{page}</LayoutWrapper>
}

MerchOrderPage.getInitialProps = async function ({ res, query }): Promise<MerchOrderPageProps> {
  let artistUsername = encodeURIComponent(query.id)
  let productId = encodeURIComponent(query.product_id)
  try {
    let publicArtistData = await fetch(`${process.env.BACKEND_URL}v1/users/username/${artistUsername}`).then(r =>
      r.json()
    )
    let productDetail = await fetch(
      `${process.env.BACKEND_URL}v1/stores/${publicArtistData.id}/items/${productId}`
    ).then(r => r.json())

    if (productDetail && publicArtistData) {
      if (productDetail.user_id !== publicArtistData.id) {
        let correctArtistData = await fetch(`${process.env.BACKEND_URL}v1/users/id/${productDetail.user_id}`).then(r =>
          r.json()
        )

        const correctPath = `/${encodeURIComponent(correctArtistData.username)}/merch/${productId}`
        if (res) {
          res.writeHead(307, { location: correctPath })
          res.end()
        } else {
          Router.replace(correctPath)
        }
      }
    }

    return {
      publicArtistData,
      productDetail,
    }
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not load artist details: ${artistUsername}: ${e.message}`,
    }
  }
}
